import { Controller } from "@hotwired/stimulus";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);

/**
 * Events Calendar Controller
 *
 * Manages event indicators on the Cally calendar component.
 * Handles timezone-aware date comparisons to show event dots on correct calendar days.
 *
 * @extends Controller
 * @property {HTMLElement} calendarTarget - The Cally calendar-date element
 * @property {Array<string>} eventDatesValue - ISO 8601 datetime strings of events
 */
export default class extends Controller {
  static targets = ["calendar"];
  static values = {
    eventDates: Array,
    eventsUrl: String,
    eventsForDateUrl: String,
  };

  /**
   * Initialize the controller and set up event indicators
   */
  connect() {
    // Initialize abort controller for fetch requests
    this.abortController = null;

    this.calendarTarget.value = dayjs().format("YYYY-MM-DD");
    this.setupEventIndicators();
    this.setupNavigationHandlers();
    this.setupDateChangeHandler();
  }

  /**
   * Configure the Cally calendar to display event indicators
   *
   * Sets up the getDayParts API to return CSS part names for dates with events.
   * Handles timezone conversion between Cally's UTC dates and local event times.
   */
  setupEventIndicators() {
    const eventDates = this.eventDatesValue || [];

    /**
     * Cally getDayParts API
     *
     * @param {Date} date - Date object from Cally (UTC midnight shown in browser timezone)
     * @returns {string} Space-separated CSS part names (e.g., "has-events")
     *
     * Note: Cally provides dates at UTC midnight but displays them in browser timezone.
     * Example: July 31 00:00 UTC displays as "July 30 17:00 PDT"
     * We must convert back to UTC to get the intended calendar date.
     */
    this.calendarTarget.getDayParts = (date) => {
      // Extract the intended calendar date from Cally's UTC representation
      const calendarDate = dayjs(date).utc().format("YYYY-MM-DD");

      // Check if any events fall on this calendar date
      const hasEvent = eventDates.some((eventDateStr) => {
        // Convert event to browser's local date
        // Example: "2025-07-31T06:00:00Z" → "2025-07-30" in PDT
        const eventLocalDate = dayjs(eventDateStr).format("YYYY-MM-DD");
        return eventLocalDate === calendarDate;
      });

      return hasEvent ? "has-events" : "";
    };
  }

  /**
   * Set up navigation button handlers for fetching events
   */
  setupNavigationHandlers() {
    const prevButton = this.calendarTarget.querySelector(
      'button[slot="previous"]',
    );
    const nextButton = this.calendarTarget.querySelector('button[slot="next"]');

    if (prevButton) {
      prevButton.addEventListener("click", () => {
        this.navigateToPreviousMonth();
      });
    }

    if (nextButton) {
      nextButton.addEventListener("click", () => {
        this.navigateToNextMonth();
      });
    }
  }

  /**
   * Get the current focused month from Cally's focusedDate
   * @returns {{year: number, month: number}} Year and month (1-indexed)
   */
  getCurrentFocusedMonth() {
    const focusedDate = this.calendarTarget.focusedDate;
    
    if (focusedDate) {
      // focusedDate is in ISO format: "2025-08-30"
      const [year, month] = focusedDate.split('-').map(Number);
      return { year, month };
    } else {
      // If no focused date, use current date
      const today = dayjs();
      return {
        year: today.year(),
        month: today.month() + 1
      };
    }
  }

  /**
   * Navigate to previous month and fetch events
   */
  navigateToPreviousMonth() {
    // Get current month from Cally's focused date before navigation
    const { year, month } = this.getCurrentFocusedMonth();
    const currentDate = dayjs().year(year).month(month - 1);
    const prevMonth = currentDate.subtract(1, "month");

    // Don't go before current month
    const today = dayjs();
    if (prevMonth.isBefore(today, "month")) {
      return;
    }

    this.fetchEventsForMonth(prevMonth.year(), prevMonth.month() + 1);
  }

  /**
   * Navigate to next month and fetch events
   */
  navigateToNextMonth() {
    // Get current month from Cally's focused date before navigation
    const { year, month } = this.getCurrentFocusedMonth();
    const currentDate = dayjs().year(year).month(month - 1);
    const nextMonth = currentDate.add(1, "month");

    this.fetchEventsForMonth(nextMonth.year(), nextMonth.month() + 1);
  }

  /**
   * Fetch events for a specific month via JSON API
   */
  async fetchEventsForMonth(year, month) {
    // Cancel any in-flight request
    if (this.abortController) {
      this.abortController.abort();
    }

    // Create new abort controller for this request
    this.abortController = new AbortController();

    try {
      const response = await fetch(
        `${this.eventsUrlValue}?year=${year}&month=${month}`,
        {
          headers: {
            Accept: "application/json",
            "X-Requested-With": "XMLHttpRequest",
          },
          credentials: "same-origin", // Include cookies for Rails session
          signal: this.abortController.signal,
        },
      );

      if (!response.ok) throw new Error("Failed to fetch events");

      const data = await response.json();

      // Update event dates and refresh indicators
      this.eventDatesValue = data.event_dates || [];
      this.setupEventIndicators();
    } catch (error) {
      // Ignore abort errors
      if (error.name !== "AbortError") {
        console.error("Error fetching events:", error);
      }
    } finally {
      // Clear the controller reference
      this.abortController = null;
    }
  }

  /**
   * Set up date change handler for loading events when a date is selected
   */
  setupDateChangeHandler() {
    this.calendarTarget.addEventListener("change", (event) => {
      const selectedDate = event.target.value;
      console.log("Date selected:", selectedDate);

      this.loadEventsForDate(selectedDate);
    });
  }

  /**
   * Load events for a specific date using Turbo
   * @param {string} date - Date in YYYY-MM-DD format
   */
  loadEventsForDate(date) {
    const { Turbo } = window;

    // Build URL using the Rails path helper value
    const url = `${this.eventsForDateUrlValue}?date=${date}`;

    // Use Turbo to update just the events_section frame
    Turbo.visit(url, { frame: "events_section", action: "replace" });
  }

  /**
   * Refresh the events section for the currently selected date
   * Called when the location filter drawer is closed
   */
  refreshEvents() {
    // Get the currently selected date from the calendar, or use today
    const selectedDate = this.calendarTarget.value || dayjs().format("YYYY-MM-DD");
    
    // Also refresh the calendar indicators for the current month
    const { year, month } = this.getCurrentFocusedMonth();
    this.fetchEventsForMonth(year, month);
    
    // Reload the events for the selected date
    this.loadEventsForDate(selectedDate);
  }

  /**
   * Clean up when controller disconnects
   */
  disconnect() {
    // Cancel any pending requests
    if (this.abortController) {
      this.abortController.abort();
    }
  }
}
